import React from "react";
import { Plus, CheckCircle2, Pencil, Trash2, AlertCircle } from "lucide-react";
import {
  MaintenanceAreaDto,
  MaintenanceTaskDto,
  maintenanceAreasApi,
  maintenanceTasksApi,
} from "../../../services/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { TaskFormDialog } from "./TaskFormDialog";
import { CompleteTaskDialog } from "./CompleteTaskDialog";
import {
  CATEGORY_LABELS, PRIORITY_LABELS, describeRecurrence, describeReminders, formatDate, isOverdue,
  intToCategory, intToPriority,
} from "../utils";

export const TasksListPage: React.FC = () => {
  const [areas, setAreas] = React.useState<MaintenanceAreaDto[]>([]);
  const [tasks, setTasks] = React.useState<MaintenanceTaskDto[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const [filterAreaId, setFilterAreaId] = React.useState<string>("");
  const [onlyOverdue, setOnlyOverdue] = React.useState(false);
  const [creating, setCreating] = React.useState(false);
  const [editing, setEditing] = React.useState<MaintenanceTaskDto | null>(null);
  const [completing, setCompleting] = React.useState<MaintenanceTaskDto | null>(null);

  const reload = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [a, t] = await Promise.all([
        maintenanceAreasApi.getAll(false),
        maintenanceTasksApi.search({
          areaId: filterAreaId || undefined,
          overdue: onlyOverdue || undefined,
        }),
      ]);
      setAreas(a);
      setTasks(t);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [filterAreaId, onlyOverdue]);

  React.useEffect(() => { void reload(); }, [reload]);

  const handleDelete = async (task: MaintenanceTaskDto) => {
    if (!window.confirm(`¿Eliminar la tarea "${task.title}"?\n\nEs un borrado lógico: la tarea queda desactivada y su historial se preserva. Podrás recuperarla desde administración si hace falta.`)) return;
    try {
      await maintenanceTasksApi.delete(task.id);
      await reload();
    } catch (e) {
      window.alert(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2 items-end">
        <div className="w-56">
          <label className="text-xs text-slate-500">Área</label>
          <Select value={filterAreaId || "_all"} onValueChange={(v) => setFilterAreaId(v === "_all" ? "" : v)}>
            <SelectTrigger><SelectValue placeholder="Todas" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">Todas</SelectItem>
              {areas.map((a) => (
                <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <label className="flex items-center gap-2 text-sm">
          <Checkbox checked={onlyOverdue} onCheckedChange={(c) => setOnlyOverdue(c === true)} />
          Solo vencidas
        </label>

        <div className="ml-auto">
          <Button onClick={() => setCreating(true)}>
            <Plus className="size-4 mr-2" /> Nueva tarea
          </Button>
        </div>
      </div>

      {error && (
        <div className="rounded border border-red-200 bg-red-50 text-red-700 px-3 py-2 text-sm">{error}</div>
      )}

      {loading ? (
        <div className="text-sm text-slate-500">Cargando…</div>
      ) : tasks.length === 0 ? (
        <Card className="p-6 text-center text-slate-500 text-sm">
          No hay tareas que coincidan.
        </Card>
      ) : (
        <div className="space-y-2">
          {tasks.map((t) => {
            const overdue = isOverdue(t);
            return (
              <Card key={t.id} className={`p-4 ${overdue ? "border-red-300 bg-red-50/40" : ""}`}>
                <div className="flex flex-wrap gap-3 items-start">
                  <div className="flex-1 min-w-[260px]">
                    <div className="flex items-center gap-2">
                      {overdue && <AlertCircle className="size-4 text-red-600" />}
                      <h3 className="font-semibold">{t.title}</h3>
                    </div>
                    <div className="text-sm text-slate-600 mt-0.5">
                      <span className="font-medium">{t.equipmentName}</span>
                      <span className="text-slate-400"> · {t.areaName}</span>
                    </div>
                    {t.description && (
                      <p className="text-sm text-slate-600 mt-1">{t.description}</p>
                    )}
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500 mt-2">
                      <span><strong>Categoría:</strong> {CATEGORY_LABELS[intToCategory(t.category)]}</span>
                      <span><strong>Prioridad:</strong> {PRIORITY_LABELS[intToPriority(t.priority)]}</span>
                      <span><strong>Recurrencia:</strong> {describeRecurrence(t)}</span>
                      <span><strong>Avisos:</strong> {describeReminders(t)}</span>
                    </div>
                  </div>

                  <div className="text-right">
                    <div className="text-xs text-slate-500">Próximo</div>
                    <div className={`font-semibold ${overdue ? "text-red-700" : ""}`}>
                      {formatDate(t.nextDueAt, false)}
                    </div>
                    {t.lastCompletedAt && (
                      <div className="text-xs text-slate-500 mt-1">
                        Última: {formatDate(t.lastCompletedAt, false)}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex gap-2 pt-3 mt-3 border-t">
                  <Button size="sm" onClick={() => setCompleting(t)}>
                    <CheckCircle2 className="size-4 mr-1.5" /> Marcar realizada
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setEditing(t)}>
                    <Pencil className="size-3.5 mr-1.5" /> Editar
                  </Button>
                  <Button size="sm" variant="ghost" className="ml-auto text-red-600 hover:text-red-700" onClick={() => handleDelete(t)}>
                    <Trash2 className="size-3.5" />
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {(creating || editing) && (
        <TaskFormDialog
          task={editing}
          onClose={() => { setCreating(false); setEditing(null); }}
          onSaved={() => { setCreating(false); setEditing(null); void reload(); }}
        />
      )}
      {completing && (
        <CompleteTaskDialog
          task={completing}
          onClose={() => setCompleting(null)}
          onSaved={() => { setCompleting(null); void reload(); }}
        />
      )}
    </div>
  );
};
